<template>
  <v-card class="td">
    <header class="td__head">
      <div class="td__head-text">
        <p class="td__eyebrow">Contabilidad</p>
        <h2 class="td__title">Comprobantes de pago</h2>
        <p class="td__scope">
          <template v-if="code">
            Consolidado <strong>{{ code }}</strong> ·
            {{ item.count || targetIds.length }} facturas
          </template>
          <template v-else-if="invoiceNumber">
            Factura <strong>{{ invoiceNumber }}</strong>
          </template>
        </p>
      </div>
      <button type="button" class="td__close" title="Cerrar" @click="close">
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="td__body">
      <!-- 1. El monto. Va aqui, en la misma pantalla del comprobante: el
           cliente no quiere pasar por otro dialogo para escribir lo que se le
           paga a la aduana. -->
      <section class="td__section">
        <p class="td__label">Monto a pagar</p>
        <div class="td__amount">
          <span class="td__currency">B/.</span>
          <input
            v-model="form.taxAmount"
            type="text"
            inputmode="decimal"
            class="td__input"
            placeholder="0.00"
            aria-label="Monto de impuestos en balboas"
            @keyup.enter="saveAmount"
          />
          <button
            class="td__btn td__btn--save td__amount-btn"
            :disabled="savingAmount || !amountDirty"
            @click="saveAmount"
          >
            <v-icon small class="td__btn-icon">save</v-icon>
            {{ savingAmount ? 'Guardando…' : 'Guardar monto' }}
          </button>
        </div>
        <p v-if="amountError" class="td__error">{{ amountError }}</p>
        <p v-else class="td__help">
          Lo que se paga a la aduana por este trámite.
          <template v-if="code">
            En un consolidado es uno solo para sus {{ item.count }} facturas.
          </template>
        </p>

        <div class="td__total">
          <span class="td__total-label">Total a pagar</span>
          <strong class="td__total-value">B/. {{ totalLabel }}</strong>
        </div>
      </section>

      <!-- 1b. La fecha del pago. Se sella sola con el primer comprobante, pero
           el pago pudo hacerse dias antes; por eso se puede corregir. Solo
           aparece cuando ya hay pago: crearla es subir el comprobante. -->
      <section v-if="canEditDate" class="td__section">
        <p class="td__label">Fecha de pago</p>
        <div class="td__amount">
          <input
            v-model="paidDate"
            type="date"
            :max="today"
            class="td__input td__input--date"
            aria-label="Fecha en que se pagó el impuesto"
            @keyup.enter="saveDate"
          />
          <button
            class="td__btn td__btn--save td__amount-btn"
            :disabled="savingDate || !dateDirty"
            @click="saveDate"
          >
            <v-icon small class="td__btn-icon">save</v-icon>
            {{ savingDate ? 'Guardando…' : 'Guardar fecha' }}
          </button>
        </div>
        <p v-if="dateError" class="td__error">{{ dateError }}</p>
        <p v-else class="td__help">
          El día en que se pagó el impuesto. Se registró sola al cargar el
          comprobante; corrígela si el pago se hizo otro día. Esta fecha decide
          cuándo el trámite entra al estado de cuenta.
        </p>
      </section>

      <!-- 2. Cargar -->
      <section class="td__section">
        <p class="td__label">Adjuntar comprobante</p>
        <upload-btn
          multiple
          dropzone
          hint="Cualquier formato: PDF, imagen, lo que necesites. Puedes subir varios."
          :invalid="submitted && !hasFiles"
          @clicked="onFilePicked"
        ></upload-btn>
        <p v-if="submitted && !hasFiles" class="td__error">
          Elige al menos un archivo para subirlo.
        </p>
        <p v-else class="td__help">
          Al subir el primer comprobante se registra la fecha de pago.
        </p>
        <!-- El aviso al cliente deja de salir en cada carga: viene marcado
             cuando este es el pago (aun no hay fecha) y desmarcado cuando solo
             se agrega o reemplaza un archivo. Antes se reenviaba siempre. -->
        <label v-if="hasFiles" class="td__check">
          <input type="checkbox" v-model="notifyClient" />
          <span>
            Avisar al cliente por correo (IMPUESTOS ADUANALES PAGOS)
          </span>
        </label>
        <button
          v-if="hasFiles"
          class="td__btn td__btn--save td__btn--block"
          :disabled="saving"
          @click="handleUpload"
        >
          <v-icon small class="td__btn-icon">cloud_upload</v-icon>
          {{ saving ? 'Subiendo…' : `Subir ${form.files.length} archivo(s)` }}
        </button>
      </section>

      <!-- 3. Cargados -->
      <section class="td__section">
        <p class="td__label">
          Comprobantes cargados
          <span class="td__count">{{ documents.length }}</span>
        </p>
        <p v-if="loadingDocs" class="td__help">Cargando…</p>
        <p v-else-if="!documents.length" class="td__help">
          Todavía no hay comprobantes de pago para este trámite.
        </p>
        <ul v-else class="td__list">
          <li v-for="doc in documents" :key="doc.id" class="td__item">
            <span class="td__item-date">{{ dateOf(doc.createdAt) }}</span>
            <a
              :href="fullPath(doc.filePath)"
              target="_blank"
              rel="noopener"
              class="td__item-name"
              :title="doc.originalName || doc.filePath"
            >
              {{ doc.originalName || doc.filePath }}
            </a>
            <span v-if="doc.uploadedBy" class="td__item-who">
              {{ doc.uploadedBy.firstName }} {{ doc.uploadedBy.lastName }}
            </span>
            <!-- Eliminar pide confirmación en el sitio: el archivo se borra
                 del disco y no se puede deshacer. -->
            <template v-if="confirming === doc.id">
              <span class="td__confirm">¿Eliminar?</span>
              <button
                class="td__mini td__mini--danger"
                :disabled="deleting"
                @click="doDelete(doc)"
              >
                Sí, eliminar
              </button>
              <button class="td__mini" @click="confirming = null">
                Cancelar
              </button>
            </template>
            <button
              v-else
              class="td__remove"
              title="Eliminar este comprobante"
              @click="confirming = doc.id"
            >
              <v-icon small>delete_outline</v-icon>
            </button>
          </li>
        </ul>
        <p v-if="documents.length" class="td__help">
          Para reemplazar uno, sube el nuevo y elimina el anterior.
        </p>
      </section>
    </div>

    <footer class="td__foot">
      <button type="button" class="td__btn td__btn--ghost" @click="close">
        Cerrar
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import UploadButton from './FileUpload'
import { getFullPath } from '@/helpers/TransitHelper'

export default {
  components: {
    'upload-btn': UploadButton,
  },
  props: {
    // { invoices: [ids], invoiceNumber?, code? }
    item: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    form: {
      invoices: [],
      files: null,
      taxAmount: '',
    },
    // El monto tal como llegó, para saber si hay algo que guardar.
    initialAmount: '',
    savingAmount: false,
    amountError: '',
    // Fecha de pago tal como llegó, para saber si hay algo que corregir.
    paidDate: '',
    initialPaidDate: '',
    savingDate: false,
    dateError: '',
    notifyClient: false,
    documents: [],
    loadingDocs: false,
    submitted: false,
    saving: false,
    // Id del documento cuya eliminación se está confirmando.
    confirming: null,
    deleting: false,
    // Si se tocó algo, al cerrar hay que refrescar el listado de atrás.
    changed: false,
  }),
  computed: {
    hasFiles() {
      return Array.isArray(this.form.files) && this.form.files.length > 0
    },
    targetIds() {
      const ids = this.item.invoices
      return Array.isArray(ids) ? ids : [ids]
    },
    code() {
      return this.item.code || null
    },
    invoiceNumber() {
      return this.item.invoiceNumber || null
    },
    amountDirty() {
      return String(this.form.taxAmount).trim() !== this.initialAmount
    },
    // La fecha solo se corrige cuando ya hay un pago registrado.
    canEditDate() {
      return !!this.item.taxPaidDate
    },
    dateDirty() {
      return this.paidDate !== '' && this.paidDate !== this.initialPaidDate
    },
    today() {
      return moment().format('YYYY-MM-DD')
    },
    totalLabel() {
      const n = parseFloat(this.form.taxAmount)
      if (Number.isNaN(n)) return '0.00'
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
  created() {
    const amount =
      this.item.taxAmount === null || typeof this.item.taxAmount === 'undefined'
        ? ''
        : String(this.item.taxAmount)
    this.initialAmount = amount
    this.form = {
      invoices: this.targetIds,
      files: null,
      taxAmount: amount,
    }
    // Este es el pago mientras no haya fecha; después solo se agrega o
    // reemplaza un archivo y no hay nada nuevo que avisarle al cliente.
    this.notifyClient = !this.item.taxPaidDate
    this.paidDate = this.item.taxPaidDate
      ? moment(this.item.taxPaidDate).format('YYYY-MM-DD')
      : ''
    this.initialPaidDate = this.paidDate
    this.loadDocuments()
  },
  methods: {
    fullPath: getFullPath,
    onFilePicked(files) {
      this.form.files = files
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    loadDocuments() {
      if (!this.targetIds.length) return
      this.loadingDocs = true
      this.$store
        .dispatch('invoices/getDocuments', {
          ids: this.targetIds.join(','),
          step: 'PAYED',
        })
        .then(docs => {
          this.documents = docs || []
        })
        .finally(() => {
          this.loadingDocs = false
        })
    },
    // El monto es del TRÁMITE: en un consolidado, uno solo para todas sus
    // facturas. Devuelve promesa para poder encadenarlo con la subida.
    saveAmount() {
      const raw = String(this.form.taxAmount).trim()
      if (raw !== '' && Number.isNaN(parseFloat(raw))) {
        this.amountError = 'El monto debe ser un número.'
        return Promise.resolve(false)
      }
      this.amountError = ''
      this.savingAmount = true
      return this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: this.targetIds[0],
          taxAmount: raw === '' ? null : raw,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.amountError = 'No se pudo guardar el monto.'
            return false
          }
          this.initialAmount = raw
          this.changed = true
          return true
        })
        .catch(() => {
          this.amountError = 'No se pudo guardar el monto.'
          return false
        })
        .finally(() => {
          this.savingAmount = false
        })
    },
    // La fecha es del TRÁMITE, igual que el monto: en un consolidado una sola
    // para todas sus facturas. Queda registrado quién la movió.
    saveDate() {
      if (!this.dateDirty) return
      this.dateError = ''
      this.savingDate = true
      this.$store
        .dispatch('invoices/updateAccounting', {
          invoiceId: this.targetIds[0],
          taxPaidDate: this.paidDate,
        })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            this.dateError =
              (response && response.data && response.data.error) ||
              'No se pudo guardar la fecha.'
            return
          }
          this.initialPaidDate = this.paidDate
          this.changed = true
        })
        .catch(() => {
          this.dateError = 'No se pudo guardar la fecha.'
        })
        .finally(() => {
          this.savingDate = false
        })
    },
    handleUpload() {
      this.submitted = true
      if (!this.hasFiles) return
      this.saving = true
      // Si quedó un monto escrito sin guardar, se guarda antes de subir: el
      // comprobante y su monto son el mismo acto para contabilidad.
      const pending = this.amountDirty
        ? this.saveAmount()
        : Promise.resolve(true)
      pending
        .then(ok => {
          if (!ok) return null
          // payInvoice marca liquidado y sella la fecha de pago la primera vez.
          return this.$store.dispatch('transit/payInvoice', {
            invoices: this.form.invoices,
            files: this.form.files,
            sendEmails: this.notifyClient ? 'true' : 'false',
          })
        })
        .then(response => {
          if (response === null) return
          this.changed = true
          this.$emit('save-docs')
        })
        .finally(() => {
          this.saving = false
        })
    },
    // Cerrar refresca el listado solo si se tocó algo: el monto, un archivo
    // nuevo o uno eliminado cambian el semáforo y la fecha de la fila.
    close() {
      this.$emit(this.changed ? 'save-docs' : 'close-docs')
    },
    doDelete(doc) {
      this.deleting = true
      this.$store
        .dispatch('invoices/deleteDocument', { id: doc.id })
        .then(response => {
          if (!response || (response.status && response.status >= 400)) {
            return
          }
          this.documents = this.documents.filter(d => d.id !== doc.id)
          // Sin comprobantes el backend borra la fecha de pago y el semáforo
          // vuelve a rojo: la fila de atrás ya no dice lo mismo.
          this.changed = true
        })
        .finally(() => {
          this.deleting = false
          this.confirming = null
        })
    },
  },
}
</script>

<style scoped>
.td {
  border-radius: 12px;
  overflow: hidden;
}

/* === Encabezado === */
.td__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #15803d;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.td__head-text {
  flex: 1;
  min-width: 0;
}
.td__eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.td__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}
.td__scope {
  margin: 4px 0 0;
  font-size: 14px;
  color: #64748b;
}
.td__scope strong {
  color: #254f87;
}
.td__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
  transition: background 0.18s ease;
}
.td__close:hover {
  background: #f1f5f9;
}
.td__close .v-icon {
  color: #64748b !important;
}

/* === Cuerpo === */
.td__body {
  padding: 16px 24px 4px;
  max-height: 62vh;
  overflow-y: auto;
}
.td__section {
  margin-bottom: 18px;
}
.td__label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
}
.td__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
}
.td__error {
  margin: 6px 0 0;
  font-size: 13px;
  color: #b91c1c;
}
.td__help {
  margin: 6px 0 0;
  font-size: 13px;
  color: #94a3b8;
}

/* === Monto === */
.td__amount {
  display: flex;
  align-items: center;
  gap: 8px;
}
.td__currency {
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 700;
  color: #475569;
}
.td__input {
  flex: 1;
  min-width: 0;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #ffffff;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  text-align: right;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.td__input:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.15);
}
.td__amount-btn {
  flex: 0 0 auto;
  height: 40px;
}
/* La fecha usa la misma caja que el monto, alineada a la izquierda: un
   dd/mm/aaaa alineado a la derecha se lee raro. */
.td__input--date {
  text-align: left;
  font-weight: 600;
}

/* Resumen: el cliente pidió ver el total debajo del monto. */
.td__total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #f1f5f9;
}
.td__total-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
}
.td__total-value {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

/* === Aviso al cliente === */
.td__check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
}
.td__check input {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
}

/* === Lista de comprobantes === */
.td__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.td__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}
.td__item-date {
  flex: 0 0 auto;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.td__item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #254f87;
  text-decoration: none;
}
.td__item-name:hover {
  text-decoration: underline;
}
.td__item-who {
  flex: 0 0 auto;
  color: #94a3b8;
}
.td__remove {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  border-radius: 4px;
  padding: 3px;
  cursor: pointer;
  line-height: 1;
  transition: background 0.15s ease;
}
.td__remove:hover {
  background: #fee2e2;
}
.td__remove .v-icon {
  color: #b91c1c !important;
}
.td__confirm {
  flex: 0 0 auto;
  color: #b91c1c;
  font-weight: 600;
}
.td__mini {
  flex: 0 0 auto;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 5px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
}
.td__mini:hover {
  background: #f1f5f9;
}
.td__mini--danger {
  border-color: #fecaca;
  color: #b91c1c;
}
.td__mini--danger:hover {
  background: #fef2f2;
}

/* === Botones === */
.td__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.td__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.td__btn:focus-visible {
  outline: 2px solid #94a3b8;
  outline-offset: 2px;
}
.td__btn--block {
  width: 100%;
  margin-top: 10px;
}
.td__btn--ghost {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #475569;
}
.td__btn--ghost:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
/* Blanco sobre #15803d = 5.02:1, cumple WCAG AA. */
.td__btn--save {
  background: #15803d;
  border-color: #15803d;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(6, 46, 22, 0.3), 0 4px 10px rgba(21, 128, 61, 0.25);
}
.td__btn--save .td__btn-icon {
  color: #ffffff !important;
}
.td__btn--save:hover:not(:disabled) {
  background: #166534;
  border-color: #166534;
}
.td__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
